export enum SbomProjectStatus {
  InPlanung = 'InPlanung',
  InArbeit = 'InArbeit',
  Uebergeben = 'Uebergeben',
  Abgeschlossen = 'Abgeschlossen',
  Archiviert = 'Archiviert'
}

export const SbomProjectStatusLabels: Record<SbomProjectStatus, string> = {
  [SbomProjectStatus.InPlanung]: 'In Planung',
  [SbomProjectStatus.InArbeit]: 'In Arbeit',
  [SbomProjectStatus.Uebergeben]: 'Übergeben',
  [SbomProjectStatus.Abgeschlossen]: 'Abgeschlossen',
  [SbomProjectStatus.Archiviert]: 'Archiviert'
};

export interface SbomProject {
  id: number;
  name: string;
  description: string;
  customerId: number;
  customerName: string;
  customerLogoPath?: string;
  status: SbomProjectStatus;
  projectNumber: string;
  plcType: string;
  plcFirmwareVersion: string;
  projectHash: string;
  hashAlgorithm: string;
  createdAt: string;
  updatedAt?: string;
  handoverDate?: string;
  notes: string;
  componentCount: number;
  documentCount: number;
  components: SbomComponent[];
  documents: SbomDocument[];
  auditLogs: SbomAuditLog[];
  securityAssessment?: SecurityAssessment;
}

export interface SbomComponent {
  id: number;
  sbomProjectId: number;
  name: string;
  version: string;
  supplier: string;
  partNumber: string;
  componentType: string;
  license: string;
  componentHash: string;
  hashAlgorithm: string;
  description: string;
  isCritical: boolean;
  endOfLife?: string;
}

export interface SbomDocument {
  id: number;
  sbomProjectId: number;
  fileName: string;
  originalFileName: string;
  contentType: string;
  fileSize: number;
  description: string;
  documentType: string;
  uploadedAt: string;
}

export const COMPONENT_TYPES = [
  'SPS-Baustein',
  'HMI-Panel',
  'Frequenzumrichter',
  'Bibliothek',
  'Firmware',
  'Sensorik',
  'Aktorik',
  'Netzwerkkomponente',
  'Safety-Modul',
  'Sonstiges'
];

export const DOCUMENT_TYPES = [
  'Schaltplan',
  'SPS-Programm',
  'HMI-Projekt',
  'Projektdokumentation',
  'Inbetriebnahmeprotokoll',
  'Stückliste',
  'Prüfprotokoll',
  'Risikobeurteilung',
  'CE-Erklärung',
  'Sonstiges'
];

export interface SbomAuditLog {
  id: number;
  sbomProjectId: number;
  action: string;
  entityType: string;
  entityName: string;
  details: string;
  userName: string;
  timestamp: string;
}

export const HASH_ALGORITHMS = [
  'SHA-256',
  'SHA-384',
  'SHA-512',
  'MD5'
];

// ───── Security Assessment Types ─────

export interface SecurityAssessment {
  id: number;
  sbomProjectId: number;
  targetSecurityLevel: number;
  achievedSecurityLevel: number;
  contextAnswers: string;
  nis2Answers: string;
  frAnswers: string;
  docChecks: string;
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
  totalRequirements: number;
  completedRequirements: number;
  requirements: SecurityRequirement[];
}

export interface SecurityRequirement {
  id: number;
  securityAssessmentId: number;
  category: string;
  requirementId: string;
  title: string;
  description: string;
  normReference: string;
  isCompleted: boolean;
  completedAt?: string;
  completedBy: string;
  priority: number;
}

export interface SecurityAssessmentCreate {
  contextAnswers: string;
  nis2Answers: string;
  frAnswers: string;
  docChecks: string;
  targetSecurityLevel: number;
  achievedSecurityLevel: number;
  requirements: SecurityRequirementCreate[];
}

export interface SecurityRequirementCreate {
  category: string;
  requirementId: string;
  title: string;
  description: string;
  normReference: string;
  priority: number;
}

// ───── IEC 62443 / NIS 2 Static Data ─────

export interface FrQuestion {
  id: string;
  text: string;
  sl_relevant: number;
}

export interface FoundationalRequirement {
  id: string;
  name: string;
  nameDe: string;
  description: string;
  questions: FrQuestion[];
}

export const FOUNDATIONAL_REQUIREMENTS: FoundationalRequirement[] = [
  {
    id: 'FR1',
    name: 'Identification & Authentication Control (IAC)',
    nameDe: 'Identifikation & Authentifizierung',
    description: 'Alle Benutzer (Menschen, Software, Geräte) müssen vor dem Zugriff identifiziert und authentifiziert werden.',
    questions: [
      { id: 'fr1_q1', text: 'Gibt es eine Benutzerauthentifizierung für den SPS-Zugang (z.B. TIA Portal Projektpasswort, CPU-Zugangsstufen)?', sl_relevant: 1 },
      { id: 'fr1_q2', text: 'Werden individuelle Benutzerkonten verwendet (statt geteilter Passwörter)?', sl_relevant: 2 },
      { id: 'fr1_q3', text: 'Ist eine Multi-Faktor-Authentifizierung für kritische Zugriffe implementiert?', sl_relevant: 3 },
      { id: 'fr1_q4', text: 'Werden Software-Prozesse und Geräte (z.B. HMI, Engineering-Stationen) authentifiziert?', sl_relevant: 2 },
    ]
  },
  {
    id: 'FR2',
    name: 'Use Control (UC)',
    nameDe: 'Nutzungskontrolle',
    description: 'Authentifizierte Benutzer dürfen nur die ihnen zugewiesenen Aktionen ausführen (Least Privilege).',
    questions: [
      { id: 'fr2_q1', text: 'Gibt es definierte Rollen/Berechtigungsstufen für den SPS-Zugriff (z.B. Beobachter, Bediener, Programmierer)?', sl_relevant: 1 },
      { id: 'fr2_q2', text: 'Ist das Least-Privilege-Prinzip umgesetzt (minimale notwendige Rechte)?', sl_relevant: 2 },
      { id: 'fr2_q3', text: 'Werden Berechtigungsänderungen dokumentiert und nachvollziehbar protokolliert?', sl_relevant: 2 },
      { id: 'fr2_q4', text: 'Gibt es eine automatische Sitzungssperre nach Inaktivität?', sl_relevant: 2 },
    ]
  },
  {
    id: 'FR3',
    name: 'System Integrity (SI)',
    nameDe: 'Systemintegrität',
    description: 'Die Integrität des IACS muss gegen unautorisierte Manipulation geschützt werden.',
    questions: [
      { id: 'fr3_q1', text: 'Werden SPS-Programmänderungen versioniert und dokumentiert?', sl_relevant: 1 },
      { id: 'fr3_q2', text: 'Gibt es Integritätsprüfungen bei der Projektübergabe (z.B. SHA-256 Hash)?', sl_relevant: 1 },
      { id: 'fr3_q3', text: 'Wird die Firmware/Software der SPS-Komponenten auf Manipulation geprüft (z.B. Checksummen)?', sl_relevant: 2 },
      { id: 'fr3_q4', text: 'Gibt es einen definierten Patch-/Update-Prozess für SPS-Firmware und Entwicklungsumgebungen?', sl_relevant: 2 },
      { id: 'fr3_q5', text: 'Wird ein Online/Offline-Vergleich nach dem Einspielen dokumentiert?', sl_relevant: 1 },
    ]
  },
  {
    id: 'FR4',
    name: 'Data Confidentiality (DC)',
    nameDe: 'Datenvertraulichkeit',
    description: 'Vertrauliche Informationen müssen vor unautorisiertem Zugriff geschützt werden.',
    questions: [
      { id: 'fr4_q1', text: 'Werden SPS-Projekte verschlüsselt gespeichert und übertragen?', sl_relevant: 2 },
      { id: 'fr4_q2', text: 'Ist die Kommunikation zwischen Engineering-Station und SPS verschlüsselt (z.B. TLS)?', sl_relevant: 2 },
      { id: 'fr4_q3', text: 'Gibt es Zugriffsschutz für Projektarchive und Dokumentation?', sl_relevant: 1 },
    ]
  },
  {
    id: 'FR5',
    name: 'Restricted Data Flow (RDF)',
    nameDe: 'Eingeschränkter Datenfluss',
    description: 'Das Steuerungssystem muss durch Zonen und Conduits segmentiert werden.',
    questions: [
      { id: 'fr5_q1', text: 'Ist das OT-Netzwerk vom IT-Netzwerk getrennt (z.B. durch Firewalls)?', sl_relevant: 1 },
      { id: 'fr5_q2', text: 'Sind Zonen und Conduits gemäß IEC 62443-3-2 definiert?', sl_relevant: 2 },
      { id: 'fr5_q3', text: 'Gibt es eine dokumentierte Netzwerktopologie mit Sicherheitszonen?', sl_relevant: 1 },
      { id: 'fr5_q4', text: 'Werden nur notwendige Kommunikationswege zwischen Zonen erlaubt (Deny by Default)?', sl_relevant: 2 },
    ]
  },
  {
    id: 'FR6',
    name: 'Timely Response to Events (TRE)',
    nameDe: 'Zeitnahe Reaktion auf Ereignisse',
    description: 'Sicherheitsvorfälle müssen erkannt, gemeldet und zeitnah behoben werden.',
    questions: [
      { id: 'fr6_q1', text: 'Gibt es ein Logging/Monitoring für sicherheitsrelevante Ereignisse im OT-Netz?', sl_relevant: 1 },
      { id: 'fr6_q2', text: 'Existiert ein Incident-Response-Plan für OT-Sicherheitsvorfälle?', sl_relevant: 1 },
      { id: 'fr6_q3', text: 'Sind Meldewege an den Kunden und (falls zutreffend) an Behörden definiert?', sl_relevant: 1 },
      { id: 'fr6_q4', text: 'Werden Audit-Logs manipulationssicher gespeichert?', sl_relevant: 2 },
    ]
  },
  {
    id: 'FR7',
    name: 'Resource Availability (RA)',
    nameDe: 'Ressourcenverfügbarkeit',
    description: 'Das IACS muss auch unter Angriff (z.B. DoS) die Anlage sicher betreiben oder in einen sicheren Zustand bringen können.',
    questions: [
      { id: 'fr7_q1', text: 'Gibt es Redundanz/Backup-Konzepte für die SPS-Programmierung?', sl_relevant: 1 },
      { id: 'fr7_q2', text: 'Ist ein Notfall-/Wiederanlaufplan für SPS-Systeme dokumentiert?', sl_relevant: 1 },
      { id: 'fr7_q3', text: 'Werden regelmäßige Backups der SPS-Projekte erstellt und deren Wiederherstellung getestet?', sl_relevant: 1 },
      { id: 'fr7_q4', text: 'Ist sichergestellt, dass Safety-Systeme (SIS) unabhängig vom Steuerungssystem funktionieren?', sl_relevant: 2 },
    ]
  }
];

export interface Nis2Measure {
  id: string;
  text: string;
  article: string;
}

export const NIS2_MEASURES: Nis2Measure[] = [
  // CRA Anhang I, Teil I – Wesentliche Cybersicherheitsanforderungen (Produkteigenschaften)
  { id: 'cra_risk_assessment', text: 'Cybersicherheits-Risikoanalyse für das gelieferte Produkt durchgeführt', article: 'CRA Anhang I, Teil I, Nr. 1' },
  { id: 'cra_no_known_vuln', text: 'Produkt wird ohne bekannte ausnutzbare Schwachstellen ausgeliefert', article: 'CRA Anhang I, Teil I, Nr. 2' },
  { id: 'cra_secure_default', text: 'Sichere Standardkonfiguration (Secure by Default) – keine Standardpasswörter, minimale offene Ports', article: 'CRA Anhang I, Teil I, Nr. 3' },
  { id: 'cra_auth_protection', text: 'Schutz gegen unbefugten Zugriff (Authentifizierung, Zugriffsschutz auf SPS/HMI)', article: 'CRA Anhang I, Teil I, Nr. 4' },
  { id: 'cra_data_integrity', text: 'Integrität von Daten, Befehlen und Programmen sichergestellt (z.B. Checksummen, Schreibschutz)', article: 'CRA Anhang I, Teil I, Nr. 5' },
  { id: 'cra_data_confidentiality', text: 'Vertraulichkeit sensibler Daten geschützt (verschlüsselte Übertragung, Projektschutz)', article: 'CRA Anhang I, Teil I, Nr. 6' },
  { id: 'cra_availability', text: 'Verfügbarkeit und Resilienz sichergestellt (Watchdog, Fail-Safe, Redundanz)', article: 'CRA Anhang I, Teil I, Nr. 7' },
  { id: 'cra_minimize_surface', text: 'Angriffsfläche minimiert (nur notwendige Dienste/Protokolle aktiv)', article: 'CRA Anhang I, Teil I, Nr. 8' },
  { id: 'cra_logging', text: 'Sicherheitsrelevante Ereignisse werden protokolliert (Logging/Audit)', article: 'CRA Anhang I, Teil I, Nr. 9' },
  { id: 'cra_secure_update', text: 'Mechanismus für sichere Updates vorhanden (Firmware-Update-Prozess)', article: 'CRA Anhang I, Teil I, Nr. 10' },
  // CRA Anhang I, Teil II – Anforderungen an die Schwachstellenbehandlung
  { id: 'cra_sbom', text: 'SBOM (Software Bill of Materials) für das gelieferte Produkt erstellt und gepflegt', article: 'CRA Art. 13 / Anhang I, Teil II' },
  { id: 'cra_vuln_handling', text: 'Prozess zur Schwachstellenbehandlung eingerichtet (Identifikation, Bewertung, Behebung)', article: 'CRA Anhang I, Teil II, Nr. 1' },
  { id: 'cra_vuln_disclosure', text: 'Koordinierte Schwachstellenoffenlegung (Coordinated Vulnerability Disclosure) möglich', article: 'CRA Anhang I, Teil II, Nr. 5' },
  { id: 'cra_security_updates', text: 'Kostenlose Sicherheitsupdates werden ohne Verzögerung bereitgestellt', article: 'CRA Anhang I, Teil II, Nr. 8' },
  // IEC 62443-2-4 – Dienstleister-Anforderungen
  { id: 'iec_sp01_handover', text: 'Übergabeprotokoll mit Integritätsnachweis (Hash + ggf. Signatur) erstellt', article: 'IEC 62443-2-4 SP.01' },
  { id: 'iec_sp02_config', text: 'Security-Konfiguration der Lösung dokumentiert (Härtung, Netzwerk, Zugriffsrechte)', article: 'IEC 62443-2-4 SP.02' },
  { id: 'iec_sp03_patch', text: 'Patch-Management-Prozess für gelieferte Komponenten definiert', article: 'IEC 62443-2-4 SP.03' },
  { id: 'iec_sp04_accounts', text: 'Account-Management: Übergebene Konten/Passwörter dokumentiert, Standardpasswörter geändert', article: 'IEC 62443-2-4 SP.04' },
  { id: 'iec_sp06_change', text: 'Änderungsmanagement-Prozess für Programmänderungen beim Kunden definiert', article: 'IEC 62443-2-4 SP.06' },
  { id: 'iec_sp07_backup', text: 'Backup der gelieferten Projektdaten erstellt und Wiederherstellung dokumentiert', article: 'IEC 62443-2-4 SP.07' },
];

export interface DocumentCheckItem {
  id: string;
  text: string;
  norm: string;
  required_sl: number;
}

export const DOCUMENTS_CHECKLIST: DocumentCheckItem[] = [
  // Pflichtdokumente für Lieferanten/Dienstleister
  { id: 'doc_sbom', text: 'Software Bill of Materials (SBOM) für gelieferte Produkte', norm: 'CRA Art. 13 / Anhang I', required_sl: 1 },
  { id: 'doc_risk', text: 'Cybersicherheits-Risikoanalyse des gelieferten Produkts', norm: 'CRA Anhang I / IEC 62443-3-2', required_sl: 1 },
  { id: 'doc_hash', text: 'Integritätsnachweis (SHA-256 Hash) der Projektübergabe', norm: 'IEC 62443-3-3 SR 3.4 / CRA Art. 10', required_sl: 1 },
  { id: 'doc_handover', text: 'Übergabeprotokoll mit Projektstands-Verifikation', norm: 'IEC 62443-2-4 SP.01', required_sl: 1 },
  { id: 'doc_hardening', text: 'Härtungsleitfaden für gelieferte SPS-/HMI-Konfiguration', norm: 'IEC 62443-2-4 SP.02 / IEC 62443-4-2', required_sl: 1 },
  { id: 'doc_config', text: 'Security-Konfigurationsdokumentation (Ports, Protokolle, Zugriffsrechte)', norm: 'IEC 62443-2-4 SP.02', required_sl: 1 },
  { id: 'doc_accounts', text: 'Übergabe-Dokumentation Benutzerkonten und Passwörter', norm: 'IEC 62443-2-4 SP.04', required_sl: 1 },
  { id: 'doc_access', text: 'Zugriffskontrollmatrix (Wer darf was auf SPS/HMI?)', norm: 'IEC 62443-3-3 FR1+FR2', required_sl: 1 },
  { id: 'doc_change', text: 'Änderungsmanagement-Prozess für Programmänderungen', norm: 'IEC 62443-2-4 SP.06', required_sl: 1 },
  { id: 'doc_patch', text: 'Patch-/Update-Prozess für gelieferte Komponenten', norm: 'IEC 62443-2-3 / CRA Anhang I, Teil II', required_sl: 1 },
  { id: 'doc_backup', text: 'Backup des gelieferten Projekts + Wiederherstellungsanleitung', norm: 'IEC 62443-2-4 SP.07', required_sl: 1 },
  { id: 'doc_network', text: 'Netzwerktopologie-Dokumentation (relevanter Ausschnitt)', norm: 'IEC 62443-3-2', required_sl: 1 },
  { id: 'doc_zones', text: 'Zonen- und Conduit-Diagramm (falls Netzwerkintegration)', norm: 'IEC 62443-3-2', required_sl: 1 },
  { id: 'doc_test', text: 'Testbericht / Abnahmeprotokoll Security-Funktionen', norm: 'IEC 62443-4-1 SM-9/SM-10', required_sl: 2 },
  { id: 'doc_vulnerability', text: 'Schwachstellenanalyse / Vulnerability Assessment', norm: 'IEC 62443-4-1 SM-6 / CRA Anhang I, Teil II', required_sl: 2 },
  { id: 'doc_crypto', text: 'Kryptographie-Konzept (falls Verschlüsselung eingesetzt)', norm: 'IEC 62443-3-3 FR4', required_sl: 2 },
  { id: 'doc_vuln_disclosure', text: 'Vulnerability Disclosure Policy', norm: 'CRA Anhang I, Teil II, Nr. 5', required_sl: 2 },
];

export interface SlDescription {
  label: string;
  color: string;
  title: string;
  description: string;
  attacker: string;
}

export const SL_DESCRIPTIONS: Record<number, SlDescription> = {
  0: { label: 'SL 0', color: '#6b7280', title: 'Kein spezifischer Schutz', description: 'Keine besonderen Sicherheitsanforderungen.', attacker: '—' },
  1: { label: 'SL 1', color: '#22c55e', title: 'Schutz gegen zufälligen/unbeabsichtigten Missbrauch', description: 'Schutz gegen Fehler, die durch ungeschulte Mitarbeiter oder zufällige Ereignisse entstehen.', attacker: 'Unbeabsichtigt / zufällig' },
  2: { label: 'SL 2', color: '#eab308', title: 'Schutz gegen vorsätzlichen Missbrauch mit einfachen Mitteln', description: 'Schutz gegen Angreifer mit geringen Ressourcen, allgemeinen Kenntnissen und geringer Motivation.', attacker: 'Geringe Ressourcen, allgemeine Skills' },
  3: { label: 'SL 3', color: '#f97316', title: 'Schutz gegen vorsätzlichen Missbrauch mit anspruchsvollen Mitteln', description: 'Schutz gegen Angreifer mit moderaten Ressourcen, IACS-spezifischen Kenntnissen und moderater Motivation.', attacker: 'Moderate Ressourcen, IACS-Know-how' },
  4: { label: 'SL 4', color: '#ef4444', title: 'Schutz gegen vorsätzliche Angriffe mit erweiterten Ressourcen', description: 'Schutz gegen staatliche Akteure oder organisierte Kriminalität mit hohen Ressourcen und hoher Motivation.', attacker: 'Erweiterte Ressourcen, hohe Motivation' },
};

export const ASSESSMENT_PHASES = [
  { id: 'welcome', label: 'Start' },
  { id: 'context', label: 'Projektkontext' },
  { id: 'cra', label: 'CRA-Pflichten' },
  { id: 'zones', label: 'Zonen & Conduits' },
  { id: 'sl_assessment', label: 'Security Level' },
  { id: 'fr_checklist', label: 'Foundational Req.' },
  { id: 'documents', label: 'Übergabe-Dokumente' },
  { id: 'result', label: 'Ergebnis' },
];
